/**
 * The system fill as indirect light (style guide §3.1 "one star, one key; everything else is fill"; D-047 lighting
 * overhaul). Planetshine from the gas giant and the nebula back-glow are large area sources, so instead of two
 * directional lights (sharp terminators and hard, violet specular hot spots on flat panels) they are projected into
 * one order-2 spherical-harmonics `LightProbe`: a soft, wrapped irradiance lobe per source that the material
 * ambient occlusion and screen-space AO modulate like every other indirect term (post/ao.ts). Specular from these
 * sources comes from the baked sky reflections instead.
 *
 * The fill and rim `DirectionalLight`s stay in the rig as invisible holders of colour, intensity and direction
 * (lookdev rigs and the dev panel move them); `stepFillProbe` re-projects them every frame, allocation-free.
 */
import { LightProbe, type DirectionalLight } from 'three/webgpu'

/** Scratch basis (9 SH coefficients); module-level so the per-frame projection never allocates. */
const basis = new Float64Array(9)

/**
 * three's real SH basis (SphericalHarmonics3.getBasisAt) at the unit direction (x, y, z), written into `out`.
 */
export function shBasis(x: number, y: number, z: number, out: Float64Array): Float64Array {
  out[0] = 0.282095
  out[1] = 0.488603 * y
  out[2] = 0.488603 * z
  out[3] = 0.488603 * x
  out[4] = 1.092548 * x * y
  out[5] = 1.092548 * y * z
  out[6] = 0.315392 * (3 * z * z - 1)
  out[7] = 1.092548 * x * z
  out[8] = 0.546274 * (x * x - y * y)
  return out
}

/**
 * Irradiance three's LightProbe returns for a normal from coefficients `c` (one channel, 9 values), using the
 * clamped-cosine convolution of SphericalHarmonics3.getIrradianceAt. Pure; used by tests to check the lobe.
 */
export function shIrradiance(c: ArrayLike<number>, x: number, y: number, z: number): number {
  const b = shBasis(x, y, z, new Float64Array(9))
  const a = [Math.PI, (2 * Math.PI) / 3, (2 * Math.PI) / 3, (2 * Math.PI) / 3, Math.PI / 4, Math.PI / 4, Math.PI / 4, Math.PI / 4, Math.PI / 4]
  let e = 0
  for (let i = 0; i < 9; i++) e += a[i] * c[i] * b[i]
  return e
}

/** Band windows (l = 0, 1, 2): soften the lobe a little so the sources read as broad, not point-like. */
export const FILL_WINDOW: readonly [number, number, number] = [1, 0.92, 0.72]

export function createFillProbe(): LightProbe {
  const probe = new LightProbe()
  probe.name = 'fillProbe'
  return probe
}

function accumulate(probe: LightProbe, light: DirectionalLight): void {
  if (light.intensity <= 0) return
  const t = light.target.position
  const p = light.position
  let dx = p.x - t.x
  let dy = p.y - t.y
  let dz = p.z - t.z
  const l = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (l < 1e-9) return
  dx /= l
  dy /= l
  dz /= l
  shBasis(dx, dy, dz, basis)
  const c = light.color
  const k = light.intensity
  const co = probe.sh.coefficients
  for (let i = 0; i < 9; i++) {
    const w = basis[i] * k * (i === 0 ? FILL_WINDOW[0] : i < 4 ? FILL_WINDOW[1] : FILL_WINDOW[2])
    co[i].x += c.r * w
    co[i].y += c.g * w
    co[i].z += c.b * w
  }
}

/**
 * Re-projects the fill and rim holders into the probe. The holders stay invisible, so they add no direct light.
 * Allocation-free.
 */
export function stepFillProbe(probe: LightProbe, fill: DirectionalLight, rim: DirectionalLight): void {
  const co = probe.sh.coefficients
  for (let i = 0; i < 9; i++) co[i].set(0, 0, 0)
  accumulate(probe, fill)
  accumulate(probe, rim)
}
